import { Box, Link, Modal, ModalBody, ModalContent, ModalOverlay } from '@homebotapp/hb-react-component-catalog'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Link as ReactRouterLink } from 'react-router-dom'
import { StatusIcon } from '../..'
import { setPricePoint } from '../../../actions/affordability'
import { updateBuyerInfo } from '../../../actions/buyerInfo'
import { fetchAverageMedianPrice, setBuyerOnboardingState } from '../../../actions/buyerOnboarding'
import { saveClientAnswer } from '../../../actions/client'
import { sortZipExplore } from '../../../actions/zipModal'
import {
  BUYER_COMPLETED_ONBOARDING,
  NEW_ORDERED_STEPS,
  NEW_STEPS,
  QUESTION_ONBOARDED
} from '../../../constants/client/onboarding'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import history from '../../../history'
import { useHomeDigestUrl } from '../../../hooks/navigation'
import {
  selectAverageMedianPrice,
  selectBuyerOnboardingState,
  selectNeedsBuyerOnboarding
} from '../../../store/selectors/buyerOnboarding'
import { selectAnswerCityCenter, selectClientId, selectSortFieldFromAnswer } from '../../../store/selectors/client'
import { selectCustomMailerVideoId } from '../../../store/selectors/customMailer'
import { selectBuyerWelcomeVideoId, selectBuyerWelcomeVideoType } from '../../../store/selectors/videos'
import { IconAngleLeft } from '../../shared/Icon'
import { WizardProvider, useWizardProvider } from '../../shared/WizardContext'
import Step from '../../shared/WizardContext/Step'
import BuyerOnboarded from './Steps/BuyerOnboarded'
import BuyerWelcomeVideo from './Steps/BuyerWelcomeVideo'
import { EnterCity } from './Steps/EnterCity/EnterCity'
import { EnterCloseDate } from './Steps/EnterCloseDate'
import EnterGoal from './Steps/EnterGoal'
import EnterPriority from './Steps/EnterPriority'
import EnterThinkingOfSellingHome from './Steps/EnterThinkingOfSellingHome'

import styles from './BuyerOnboardingWizard.module.scss'
import { selectCustomerProfileSettingsLoading } from '../../../store/selectors/customerProfileSettings'
import { selectCustomerId } from '../../../store/selectors/customerProfile'

const getProgress = step => {
  const range = 100 / (NEW_ORDERED_STEPS.length - 1)
  const index = NEW_ORDERED_STEPS.indexOf(step)
  return Math.round(index * range)
}

const WizardModal = () => {
  const dispatch = useDispatch()
  const { currentStep, loading, goForward } = useWizardProvider()
  const modalShow = useSelector(selectNeedsBuyerOnboarding)
  const latLng = useSelector(selectAnswerCityCenter)
  const averageMedianPrice = useSelector(selectAverageMedianPrice)
  const videoId = useSelector(selectBuyerWelcomeVideoId)
  const videoType = useSelector(selectBuyerWelcomeVideoType)
  const clientId = useSelector(selectClientId)
  const customerProfileId = useSelector(selectCustomerId)
  const pathName = SEARCH_PATH_HELPERS.forYouTab.buildPath(customerProfileId, clientId)
  const sortField = useSelector(selectSortFieldFromAnswer)
  const customMailerVideoId = useSelector(selectCustomMailerVideoId)
  const homeDigestUrl = useHomeDigestUrl()
  const [tuneValue, setTuneValue] = useState()

  const handleClose = () => {
    dispatch(setBuyerOnboardingState(BUYER_COMPLETED_ONBOARDING))

    const forYouPathWithNoSearchParams = `/for-you/${customerProfileId}`
    if (!customMailerVideoId) {
      // console.log('pathName when clicking get started :', pathName)
      history.push({
        pathname: forYouPathWithNoSearchParams,
        search: `?clientId=${clientId}`,
        state: {
          // @ts-ignore
          ...history.location.state,
          onboardingCenter: latLng
        }
      })
    } else {
      // Coming from video email as buyer
      history.push({ pathname: forYouPathWithNoSearchParams, search: `?clientId=${clientId}` })
    }
  }

  const persistBuyerOnboarded = () => {
    dispatch(
      saveClientAnswer({
        question: QUESTION_ONBOARDED,
        response: {
          value: true
        }
      })
    )

    dispatch(sortZipExplore(sortField))
    dispatch(setPricePoint(tuneValue))
    dispatch(
      updateBuyerInfo({
        affordability: tuneValue,
        affordabilityMethod: 'pricePoint'
      })
    )
  }

  const handleForwardNavFromTune = () => {
    const shouldNavNextStep = !!videoId && !!videoType
    if (shouldNavNextStep) {
      // @ts-ignore
      goForward()
    } else {
      handlePersistAndCloseFromVideo()
    }
  }

  const handlePersistAndCloseFromVideo = () => {
    persistBuyerOnboarded()
    handleClose()
  }

  useEffect(() => {
    if (!!latLng && !averageMedianPrice) {
      dispatch(fetchAverageMedianPrice(latLng))
    }
  })

  return (
    <Modal onOverlayClick={() => null} isOpen={modalShow} onClose={() => null}>
      <ModalOverlay />
      <ModalContent>
        <Box height='5px' width={`${getProgress(currentStep) || 0}%`} backgroundColor='brand.600' borderTopRadius={3} />
        {!!homeDigestUrl && (
          <Box padding={3}>
            <Link as={ReactRouterLink} to={homeDigestUrl}>
              <IconAngleLeft className={styles.spacingWrapper} />
              <FormattedMessage id='BuyerOnboardingWizard.modal.backButton' defaultMessage='Back to Home Digest' />
            </Link>
          </Box>
        )}
        <ModalBody paddingBottom={10}>
          {/* @ts-ignore */}
          {loading && <StatusIcon spin />}
          <Step step={NEW_STEPS.BUYER_CITY}>
            <EnterCity />
          </Step>
          <Step step={NEW_STEPS.BUYER_GOALS}>
            <EnterGoal />
          </Step>
          <Step step={NEW_STEPS.BUYER_THINKING_OF_SELLING_HOME}>
            <EnterThinkingOfSellingHome />
          </Step>
          <Step step={NEW_STEPS.BUYER_CLOSE_DATE}>
            <EnterCloseDate />
          </Step>
          <Step step={NEW_STEPS.BUYER_PRIORITY}>
            <EnterPriority />
          </Step>
          <Step step={NEW_STEPS.BUYER_ONBOARDED}>
            <BuyerOnboarded handleSubmitClick={handleForwardNavFromTune} setWizardTuneValue={setTuneValue} />
          </Step>
          <Step step={NEW_STEPS.BUYER_WELCOME_VIDEO}>
            <BuyerWelcomeVideo handleSubmitClick={handlePersistAndCloseFromVideo} />
          </Step>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const BuyerOnboardingWizard = () => {
  const onboardingState = useSelector(selectBuyerOnboardingState)
  const [initialStep, setInitialStep] = useState(onboardingState)

  if (onboardingState && !initialStep) {
    setInitialStep(onboardingState)
  }

  return (
    <WizardProvider steps={NEW_ORDERED_STEPS} initialStep={initialStep}>
      <WizardModal />
    </WizardProvider>
  )
}
